import React, { useState, useMemo, useEffect } from "react";
import { motion, AnimatePresence } from "framer-motion";
import { Sparkles, X, RefreshCw, Hand, Star, Crown } from "lucide-react";
import CardFace from "./CardFace";
import Confetti from "./Confetti";
import { rarityMeta, RARITY_FULL, rarityTier, TIER_LABEL } from "../lib/helpers";

const reduced =
  typeof window !== "undefined" &&
  window.matchMedia &&
  window.matchMedia("(prefers-reduced-motion: reduce)").matches;

const TIER_RANK = { epic: 1, legendary: 2 };

export default function PackOpening({ result, setName, imagesEnabled, onClose, onReopen, canReopen }) {
  const cards = useMemo(() => result?.cards || [], [result]);
  const [phase, setPhase] = useState("reveal");
  const [revealed, setRevealed] = useState(() => new Set());
  const [burst, setBurst] = useState(null); // {tier, id}

  const allRevealed = revealed.size >= cards.length;
  const topTier = useMemo(() => {
    let best = null;
    cards.forEach((c) => {
      const t = rarityTier(c);
      if (t && (!best || TIER_RANK[t] > TIER_RANK[best])) best = t;
    });
    return best;
  }, [cards]);

  useEffect(() => {
    if (!burst || reduced) return;
    const t = setTimeout(() => setBurst(null), 2200);
    return () => clearTimeout(t);
  }, [burst]);

  const triggerBurst = (tier) => {
    if (!tier || reduced) return;
    setBurst({ tier, id: Date.now() });
  };

  const revealOne = (i) => {
    if (revealed.has(i)) return;
    setRevealed((p) => new Set(p).add(i));
    triggerBurst(rarityTier(cards[i]));
  };
  const revealAll = () => {
    setRevealed(new Set(cards.map((_, i) => i)));
    triggerBurst(topTier);
  };

  const newCount = cards.filter((c) => c.is_new).length;
  const dupCount = cards.length - newCount;

  return (
    <div className="fixed inset-0 z-50 bg-[#0b1324]/80 backdrop-blur-sm flex items-end sm:items-center justify-center" data-testid="pack-opening">
      {burst && !reduced && <Confetti key={burst.id} tier={burst.tier} />}
      <motion.div
        initial={{ y: 40, opacity: 0 }}
        animate={{ y: 0, opacity: 1 }}
        className="w-full max-w-md sm:max-w-lg bg-[#eef8ff] rounded-t-3xl sm:rounded-3xl max-h-[92vh] overflow-y-auto no-scrollbar"
      >
        <div className="sticky top-0 bg-[#eef8ff]/95 backdrop-blur px-4 py-3 flex items-center justify-between border-b border-sky-100">
          <div className="font-display font-bold text-gradient">{setName}</div>
          <button onClick={onClose} data-testid="pack-close-btn" className="pill-soft w-9 h-9 flex items-center justify-center">
            <X size={18} />
          </button>
        </div>

        <div className="p-4 pb-6">
          <AnimatePresence mode="wait">
            {phase === "peel" && (
              <motion.div
                key="peel"
                exit={{ opacity: 0, scale: 0.9 }}
                className="flex flex-col items-center justify-center py-10"
              >
                <motion.div
                  initial={{ rotate: -4, y: 0 }}
                  animate={{ rotate: [-4, 4, -2, 0], y: [0, -8, 0] }}
                  transition={{ duration: 1.2, repeat: Infinity }}
                  className="relative w-40 h-56 rounded-2xl overflow-hidden shadow-2xl"
                  style={{ background: "linear-gradient(150deg,#8b5cf6,#ec4899 55%,#38bdf8)" }}
                >
                  <div className="absolute inset-0 foil-sheen" />
                  <div className="absolute inset-0 flex flex-col items-center justify-center text-white">
                    <Sparkles size={34} />
                    <span className="font-display font-bold mt-2 text-sm tracking-wide">LUMACOFONO</span>
                  </div>
                </motion.div>
                <p className="mt-5 font-bold luma-muted text-sm">Apertura in corso…</p>
                <button onClick={() => setPhase("reveal")} data-testid="skip-animation-btn" className="pill-soft mt-3 px-4 py-2 text-sm">
                  Salta animazione
                </button>
              </motion.div>
            )}

            {phase === "reveal" && (
              <motion.div key="reveal" initial={{ opacity: 0 }} animate={{ opacity: 1 }}>
                <div className="flex items-center justify-between mb-3">
                  <p className="text-sm font-bold luma-muted flex items-center gap-1.5">
                    <Hand size={15} /> Tocca per scoprire
                  </p>
                  <button onClick={revealAll} data-testid="reveal-all-btn" className="pill-soft px-3 py-1.5 text-sm">
                    Rivela tutte
                  </button>
                </div>

                <div className="grid grid-cols-3 gap-2.5">
                  {cards.map((c, i) => {
                    const open = revealed.has(i);
                    const tier = rarityTier(c);
                    const legendary = tier === "legendary";
                    return (
                      <div key={i} className="relative" style={{ perspective: 800 }}>
                        <motion.div
                          onClick={() => !open && revealOne(i)}
                          className="relative w-full aspect-[5/7] cursor-pointer"
                          data-testid={`reveal-card-${i}`}
                          style={{ transformStyle: "preserve-3d" }}
                          animate={{ rotateY: open ? 180 : 0 }}
                          transition={{ duration: reduced ? 0 : 0.5 }}
                        >
                          <div className="absolute inset-0 card-back rounded-2xl flex items-center justify-center" style={{ backfaceVisibility: "hidden" }}>
                            <Sparkles className="text-white/80" size={22} />
                          </div>
                          <div className="absolute inset-0" style={{ backfaceVisibility: "hidden", transform: "rotateY(180deg)" }}>
                            {tier && open && (
                              <motion.div
                                initial={{ opacity: 0, scale: 0.85 }}
                                animate={reduced ? { opacity: 0.85 } : { opacity: [0.55, 1, 0.75], scale: [0.95, 1.08, 1] }}
                                transition={reduced ? {} : { duration: 1.6, repeat: Infinity }}
                                className={`absolute -inset-1.5 rounded-2xl blur-[7px] z-0 ${
                                  legendary
                                    ? "bg-gradient-to-tr from-amber-300 via-yellow-200 to-orange-400"
                                    : "bg-gradient-to-tr from-fuchsia-400 via-pink-300 to-sky-400"
                                }`}
                              />
                            )}
                            <div className={`relative z-10 rounded-2xl ${tier && open ? (legendary ? "ring-2 ring-amber-300" : "ring-2 ring-fuchsia-300") : ""}`}>
                              <CardFace card={c} imagesEnabled={imagesEnabled} />
                              {tier && open && !reduced && (
                                <>
                                  <motion.div className="absolute inset-0 z-20 rounded-2xl foil-sheen pointer-events-none" />
                                  <motion.div
                                    initial={{ opacity: 0, y: 6, scale: 0.8 }}
                                    animate={{ opacity: 1, y: 0, scale: 1 }}
                                    className={`absolute -top-2 left-1/2 -translate-x-1/2 z-30 chip !text-[9px] !px-2 !py-0.5 whitespace-nowrap shadow-lg flex items-center gap-1 ${
                                      legendary ? "bg-amber-400 text-amber-950" : "bg-fuchsia-500 text-white"
                                    }`}
                                  >
                                    {legendary ? <Crown size={10} /> : <Star size={10} />} {TIER_LABEL[tier]}
                                  </motion.div>
                                </>
                              )}
                            </div>
                          </div>
                        </motion.div>
                        {open && (
                          <div className="mt-1 flex justify-center">
                            <span className={`chip ${c.is_new ? "bg-emerald-100 text-emerald-700 border-emerald-200" : "bg-amber-100 text-amber-700 border-amber-200"} !text-[10px] !px-2 !py-0.5`}>
                              {c.is_new ? "Nuova" : "Doppione"}
                            </span>
                          </div>
                        )}
                      </div>
                    );
                  })}
                </div>

                {topTier && (
                  <motion.div
                    initial={{ opacity: 0, y: 8 }}
                    animate={{ opacity: 1, y: 0 }}
                    className={`mt-4 flex items-center justify-center gap-1.5 font-bold text-sm ${topTier === "legendary" ? "text-amber-600" : "text-fuchsia-600"}`}
                  >
                    {topTier === "legendary" ? <Crown size={16} /> : <Sparkles size={16} />}
                    {topTier === "legendary" ? "Hai trovato una carta leggendaria!" : "Carta super rara in questa bustina!"}
                  </motion.div>
                )}

                <button
                  onClick={() => setPhase("summary")}
                  disabled={!allRevealed}
                  data-testid="go-summary-btn"
                  className="pill-primary w-full mt-5 py-3.5 text-base disabled:cursor-not-allowed"
                >
                  {allRevealed ? "Vedi riepilogo" : `Scopri le carte (${revealed.size}/${cards.length})`}
                </button>
              </motion.div>
            )}

            {phase === "summary" && (
              <motion.div key="summary" initial={{ opacity: 0, y: 10 }} animate={{ opacity: 1, y: 0 }}>
                <div className="flex items-center justify-center gap-3 mb-4">
                  <span className="chip bg-emerald-100 text-emerald-700 border-emerald-200">{newCount} Nuove</span>
                  <span className="chip bg-amber-100 text-amber-700 border-amber-200">{dupCount} Doppioni</span>
                </div>

                <div className="space-y-2">
                  {cards.map((c, i) => {
                    const meta = rarityMeta(c.rarity);
                    const tier = rarityTier(c);
                    return (
                      <div
                        key={i}
                        className={`luma-card !rounded-2xl p-2.5 flex items-center gap-3 relative overflow-hidden ${
                          tier ? (tier === "legendary" ? "ring-2 ring-amber-300" : "ring-2 ring-fuchsia-300") : ""
                        }`}
                        data-testid={`summary-card-${i}`}
                      >
                        {tier && !reduced && <div className="absolute inset-0 foil-sheen pointer-events-none opacity-60" />}
                        <div className={`w-11 shrink-0 relative z-10 rounded-xl ${tier ? "ring-1 ring-white/60" : ""}`}>
                          <CardFace card={c} imagesEnabled={imagesEnabled} />
                        </div>
                        <div className="min-w-0 flex-1 relative z-10">
                          <div className="font-bold text-sm truncate">{c.name}</div>
                          <div className="text-xs luma-muted font-mono">{c.number} · {RARITY_FULL[c.rarity] || c.source_rarity}</div>
                        </div>
                        <span className={`chip ${meta.cls} !text-[10px] relative z-10`}>{meta.label}</span>
                        <span className={`chip !text-[10px] relative z-10 ${c.is_new ? "bg-emerald-100 text-emerald-700 border-emerald-200" : "bg-amber-100 text-amber-700 border-amber-200"}`}>
                          {c.is_new ? "Nuova" : "Doppione"}
                        </span>
                      </div>
                    );
                  })}
                </div>

                <div className="flex gap-2.5 mt-5">
                  <button onClick={onClose} data-testid="summary-close-btn" className="pill-soft flex-1 py-3 text-sm">
                    Chiudi
                  </button>
                  <button
                    onClick={onReopen}
                    disabled={!canReopen}
                    data-testid="summary-reopen-btn"
                    className="pill-primary flex-1 py-3 text-sm flex items-center justify-center gap-2"
                  >
                    <RefreshCw size={16} /> Apri un'altra
                  </button>
                </div>
              </motion.div>
            )}
          </AnimatePresence>
        </div>
      </motion.div>
    </div>
  );
}
